/**
 * 注文確認 Modal (v0.5)。
 *
 * 进入时立即调用 preview；用户输码后 debounce 再调；
 * 「Stripeで支払う」根据 promo 状态分流到老/新 endpoint。
 *
 * 详见 docs/kaga-coupon/02-技術方案_加賀電子クーポン発行機能.md §5.5
 */
import { Alert, Badge, Button, Card, Divider, Group, Modal, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconAlertCircle, IconCircleCheck, IconCircleX, IconGift, IconHeadset } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createCheckoutSession,
  createCheckoutSessionWithPromo,
  listConsultingServices,
  previewCheckout,
  type CheckoutPreviewResponse,
  type ConsultingServiceInfo,
} from '@mygpt/app';

interface Props {
  opened: boolean;
  priceId: string;
  initialPromo?: string | null;
  successUrl: string;
  cancelUrl: string;
  onClose: () => void;
}

const formatJpy = (amount: number) => `¥${amount.toLocaleString()}`;
// 强制按 JST 显示日期：trial_end/billing 都是日本时区的次月 1 日 00:00，
// 浏览器若在 UTC 等其他时区会显示前一日（如 5/31 而不是 6/1），统一 'Asia/Tokyo' 避免混淆
const formatDate = (s: string) =>
  new Date(s).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
// 业务上 trial_end = 次月 1 日 01:00 JST = 实际 trial 最后一天为「上月末日」(5/31)，
// 给用户看的「本日~XX」需要扣 1 天得到 trial 最后一天
const formatTrialLastDay = (s: string) => {
  const d = new Date(s);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

export const OrderConfirmModal = ({ opened, priceId, initialPromo, successUrl, cancelUrl, onClose }: Props) => {
  const { t } = useTranslation();

  const [promo, setPromo] = useState<string>(initialPromo || '');
  const [debouncedPromo] = useDebouncedValue(promo, 300);
  const [preview, setPreview] = useState<CheckoutPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // v0.8: 多咨询服务加购 —— 列出所有 active service，每个独立 quantity
  const [consultingCfgs, setConsultingCfgs] = useState<ConsultingServiceInfo[]>([]);
  const [consultingQtys, setConsultingQtys] = useState<Record<string, number>>({});
  const [debouncedConsultingQtys] = useDebouncedValue(consultingQtys, 300);

  useEffect(() => {
    if (!opened) return;
    setPromo(initialPromo || '');
    setConsultingQtys({});
    listConsultingServices()
      .then((res) => setConsultingCfgs(res.data || []))
      .catch(() => setConsultingCfgs([]));
  }, [opened, initialPromo, priceId]);

  const consultingItemsPayload = useMemo(
    () =>
      Object.entries(debouncedConsultingQtys)
        .filter(([, qty]) => qty > 0)
        .map(([service_id, quantity]) => ({ service_id, quantity })),
    [debouncedConsultingQtys]
  );

  useEffect(() => {
    if (!opened) return;
    setLoading(true);
    previewCheckout({
      price_id: priceId,
      promo: debouncedPromo || undefined,
      consulting_items: consultingItemsPayload,
    })
      .then((res) => setPreview(res.data))
      .catch(() => setPreview(null))
      .finally(() => setLoading(false));
  }, [opened, priceId, debouncedPromo, consultingItemsPayload]);

  const validPromo = preview?.promo_status === 'valid';

  const promoFeedback = useMemo(() => {
    if (!preview) return null;
    const s = preview.promo_status;
    if (s === 'none') return null;
    if (s === 'valid' && preview.discount) {
      return {
        color: 'green',
        icon: <IconCircleCheck size={16} />,
        text: t('order_modal.promo_status.valid', { dealer: preview.discount.dealer_name }),
      };
    }
    const errMap: Record<string, string> = {
      not_found: 'order_modal.promo_status.not_found',
      expired: 'order_modal.promo_status.expired',
      used_up: 'order_modal.promo_status.used_up',
      wrong_customer: 'order_modal.promo_status.wrong_customer',
      inactive: 'order_modal.promo_status.inactive',
    };
    return {
      color: 'red',
      icon: <IconCircleX size={16} />,
      text: t(errMap[s] || 'order_modal.promo_status.not_found', {
        date: preview.discount ? formatDate(preview.trial?.trial_end_at || '') : '',
      }),
    };
  }, [preview, t]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let checkoutUrl: string | undefined;
      if (validPromo && promo) {
        const res = await createCheckoutSessionWithPromo({
          price_id: priceId,
          promo,
          success_url: successUrl,
          cancel_url: cancelUrl,
          consulting_items: consultingItemsPayload,
        });
        checkoutUrl = res.data.checkout_url;
      } else {
        // 既有 endpoint，自然人路径：v0.8 起也支持 consulting_items 透传（空列表 = 和老行为一致）
        const res = await createCheckoutSession({
          price_id: priceId,
          success_url: successUrl,
          cancel_url: cancelUrl,
          consulting_items: consultingItemsPayload,
        });
        checkoutUrl = res.data.checkout_url;
      }
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error('No checkout url returned');
      }
    } catch (e: any) {
      showNotification({
        color: 'red',
        message: e?.response?.data?.detail || e?.message || t('order_modal.submit_failed'),
      });
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('order_modal.title')} size="md">
      <Stack>
        {/* Plan */}
        <Card withBorder p="sm" radius="md">
          <Text size="sm" c="dimmed">
            {t('order_modal.plan')}
          </Text>
          <Text fw={600} size="lg">
            {preview?.plan_name || '...'}
          </Text>
        </Card>

        {/* Promo input */}
        <div>
          <Text size="sm" fw={500} mb={4}>
            {t('order_modal.promo_field_label')}
          </Text>
          <Group gap="xs">
            <TextInput
              placeholder={t('order_modal.promo_placeholder') as string}
              value={promo}
              onChange={(e) => setPromo(e.currentTarget.value.toUpperCase())}
              flex={1}
              styles={{ input: { fontFamily: 'monospace' } }}
              rightSection={
                validPromo ? (
                  <Badge size="sm" color="green">
                    ✓
                  </Badge>
                ) : null
              }
              rightSectionWidth={40}
            />
          </Group>
          {promoFeedback && (
            <Text size="xs" c={promoFeedback.color} mt={4}>
              <Group gap={4} wrap="nowrap">
                {promoFeedback.icon}
                {promoFeedback.text}
              </Group>
            </Text>
          )}
        </div>

        {/* v0.8 多咨询服务加购 —— 每个 active service 一行 */}
        {consultingCfgs.length > 0 && (
          <Stack gap="xs">
            {consultingCfgs.map((cfg) => (
              <Card key={cfg.id} withBorder p="sm" radius="md" bg="indigo.0">
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={2} flex={1}>
                    <Group gap="xs">
                      <IconHeadset size={16} />
                      <Text fw={600} size="sm">
                        {cfg.name}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {t('order_modal.consulting_hint', { unit: formatJpy(cfg.unit_amount) })}
                    </Text>
                    {cfg.description && (
                      <Text size="xs" c="dimmed">
                        {cfg.description}
                      </Text>
                    )}
                  </Stack>
                  <NumberInput
                    value={consultingQtys[cfg.id] ?? 0}
                    onChange={(v) =>
                      setConsultingQtys((prev) => ({
                        ...prev,
                        [cfg.id]: typeof v === 'number' ? v : 0,
                      }))
                    }
                    min={0}
                    max={cfg.max_quantity_per_subscription}
                    w={100}
                    aria-label={t('order_modal.consulting_quantity_label') as string}
                  />
                </Group>
              </Card>
            ))}
          </Stack>
        )}

        <Divider label={t('order_modal.breakdown_title')} labelPosition="left" />

        {/* Breakdown */}
        {preview && (
          <Stack gap="xs">
            <Group justify="space-between">
              <Text>{t('order_modal.standard_price')}</Text>
              <Text>{formatJpy(preview.standard_amount)}</Text>
            </Group>
            {preview.discount && (
              <Group justify="space-between">
                <Text c="green">{t('order_modal.discount')}</Text>
                <Text c="green">
                  - {formatJpy(preview.discount.amount)}
                  {preview.standard_amount > 0 && (
                    <Text component="span" size="xs" c="green" ml={4}>
                      ({Math.round((preview.discount.amount / preview.standard_amount) * 100)}% OFF)
                    </Text>
                  )}
                </Text>
              </Group>
            )}
            {preview.consulting_items &&
              preview.consulting_items.length > 0 &&
              preview.consulting_items.map((line, i) => (
                <Group key={i} justify="space-between">
                  <Text>
                    {line.name} × {line.quantity}
                    <Text component="span" size="xs" c="dimmed" ml={6}>
                      {t('order_modal.consulting_no_discount')}
                    </Text>
                  </Text>
                  <Text>{formatJpy(line.subtotal)}</Text>
                </Group>
              ))}
            <Divider />
            {validPromo && preview.recurring ? (
              <Group justify="space-between">
                <Text fw={600}>{t('order_modal.monthly_total')}</Text>
                <Text fw={600} size="lg">
                  {formatJpy(preview.recurring.amount)}
                </Text>
              </Group>
            ) : (
              <Group justify="space-between">
                <Text fw={600}>{t('order_modal.today_charge')}</Text>
                <Text fw={600} size="lg">
                  {formatJpy(preview.first_charge.amount)}
                </Text>
              </Group>
            )}
          </Stack>
        )}

        {/* Trial info (only if dealer channel) */}
        {validPromo && preview?.trial && (
          <Alert icon={<IconGift size={18} />} color="indigo" variant="light">
            <Text fw={600}>{t('order_modal.trial_title')}</Text>
            <Text size="sm">
              {t('order_modal.trial_free_until', { date: formatTrialLastDay(preview.trial.trial_end_at) })}
            </Text>
            <Text size="sm">
              {t('order_modal.first_charge_date')}: {formatDate(preview.first_charge.charged_at)}
            </Text>
            <Text size="sm">
              {t('order_modal.first_charge_amount')}: {formatJpy(preview.first_charge.amount)}
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {t('order_modal.recurring_note', { amount: formatJpy(preview.recurring.amount) })}
            </Text>
          </Alert>
        )}

        {/* Inline error fallback */}
        {!loading && !preview && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {t('order_modal.preview_failed')}
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t('order_modal.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting}
            disabled={loading || !preview || (promo.length > 0 && !validPromo)}
          >
            {t('order_modal.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
